import {
  Component,
  Input,
  OnDestroy,
  ViewEncapsulation,
} from "@angular/core";
import { FormGroup } from "@angular/forms";

import { MatDialog } from "@angular/material/dialog";

import { EMPTY, Observable, Subscription } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  AppState,
  loadedTenantState,
  priceSignTypesLoadedState,
  sitePriceSignsLoadedState,
} from "src/app/store/reducers";
import {
  getPriceSignTypesList,
  getSitePriceSignList,
} from "src/app/store/actions/sites/sites.actions";

import {
  PriceSignType,
  SitePriceSignDTO,
} from "src/app/models/Sites/sitePriceSignDTO";
import { getColorByBgColor } from "src/app/shared/utils/colorUtils";

import { PriceSignFormDialogComponent } from "./dialog/price-sign-form-dialog.component";
import { SiteProductDTO } from '../../../models/Sites/siteProductDTO';
import { SitesService } from '../../../shared/services/sites/sites.service';
import { SnackbarService } from '../../../shared/services/snackbar/snackbar.service';
import { TranslateService } from "@ngx-translate/core";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

@Component({
  selector: "site-price-signs",
  templateUrl: "./price-signs.component.html",
  styleUrls: ["./price-signs.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PriceSignsComponent implements OnDestroy {
  _siteCode: string;

  @Input() set siteCode(code: string) {
    this._siteCode = code;
    if (this._siteCode) {
      this.loadPriceSigns(this._siteCode);
      this.loadFuelProductList(this._siteCode);
      this.loadPriceSignTypes();
    }
  }

  @Input() permissionResource: string;

  priceSignList$: Observable<SitePriceSignDTO[]>;
  priceSignList: SitePriceSignDTO[];
  priceSignSub: Subscription;
  currentPriceSign: SitePriceSignDTO;

  //Grades List
  siteProductList$: Observable<SiteProductDTO[]>;
  siteProductList: SiteProductDTO[];
  siteProductSub: Subscription;

  //Price Sign Types list
  priceSignTypeList$: Observable<PriceSignType[]>;
  priceSignTypeList: PriceSignType[];
  priceSignTypeSub: Subscription;

  //Coin symbol
  tenantCoinSymbol$: Observable<string>;

  priceSignDataForm: FormGroup;
  isPriceSignSelected = false;

  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog,
    private sitesService: SitesService,
    private snackbarService: SnackbarService,
    private translate: TranslateService

  ) {
    this.priceSignList$ = this.store.pipe(select(sitePriceSignsLoadedState));
    this.priceSignTypeList$ = this.store.pipe(
      select(priceSignTypesLoadedState),
    );
    this.tenantCoinSymbol$ = this.store
      .pipe(select(loadedTenantState))
      .pipe(map(({ currency }) => currency?.symbol));
  }

  loadFuelProductList(code: string) {
    this.sitesService.getSiteFuelProductList(code).pipe(
      map((result) =>{
        const products = result.items;
        this.siteProductList = products
        return products
      }
      ),
      catchError((error) => {
        this.snackbarService.show({
          message: this.translate.instant(error),
          type: SnackbarType.ERROR
        });
        return EMPTY;
      })
    ).subscribe()
  }

  loadPriceSigns(code: string) {
    this.priceSignSub = this.priceSignList$.subscribe(
      (priceSigns: SitePriceSignDTO[]) => {
        this.priceSignList = priceSigns;
        if (priceSigns && priceSigns.length && this.currentPriceSign) {
          const { code: currentPriceSignCode } = this.currentPriceSign;
          this.currentPriceSign = priceSigns.find(
            ({ code }) => code === currentPriceSignCode,
          );
        }
        this.resetPriceSign();
      },
    );
    this.store.dispatch(
      getSitePriceSignList({
        code,
        pageReq: { realSize: true, offset: 0, limit: 999 },
      }),
    );
  }

  loadPriceSignTypes() {
    this.priceSignTypeSub = this.priceSignTypeList$.subscribe(
      (priceSignTypes: PriceSignType[]) => {
        this.priceSignTypeList = priceSignTypes;
      },
    );
    this.store.dispatch(getPriceSignTypesList(null));
  }

  getChipForeColor(color: string) {
    return getColorByBgColor(`#${color}`);
  }
  
  getCssVariable(variable: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(variable)
      .trim();
  }

  resetPriceSign() {
    this.dialog.closeAll();
    this.isPriceSignSelected = false;
    this.currentPriceSign = null;
  }

  selectPriceSign(priceSign: SitePriceSignDTO) {
    this.isPriceSignSelected = true;
    this.currentPriceSign = priceSign;

    this.openPriceSignFormDialog(false);
  }

  create() {
    this.isPriceSignSelected = false;
    this.currentPriceSign = null;

    this.openPriceSignFormDialog(true);
  }

  openPriceSignFormDialog(isCreate: boolean) {
    this.dialog.open(PriceSignFormDialogComponent, {
      autoFocus: false,
      data: {
        isCreate,
        siteCode: this._siteCode,
        currentPriceSign: this.currentPriceSign,
        priceSignTypeList: this.priceSignTypeList,
        siteProductList: this.siteProductList,
      },
    });
  }

  ngOnDestroy(): void {
    if (this.priceSignSub) {
      this.priceSignSub.unsubscribe();
    }
    if (this.siteProductSub) {
      this.siteProductSub.unsubscribe();
    }
    if (this.priceSignTypeSub) {
      this.priceSignTypeSub.unsubscribe();
    }
  }
}
